import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import { runAz } from "../../lib/az.js";
import {
  DEFAULT_IMAGE_REPO,
  fetchPublicTags,
  parseContainerAppResourceId,
  planUpdate,
  summarizePlan,
} from "../../lib/platform-update.js";
import { renderJson, renderKeyValueBlock, resolveOutputMode } from "../../lib/output.js";

export class PlatformStatusCommand extends M8tCommand {
  static paths = [["platform", "status"]];
  static usage = Command.Usage({
    description: "Show the deployed platform image vs the newest published version.",
    details:
      "Discovers the live gateway, reads its current image tag, and compares it to the newest vX.Y.Z published on the public GHCR repo. Read-only — never rolls a revision.",
  });

  subscription = Option.String("--subscription");
  imageRepo = Option.String("--image-repo", DEFAULT_IMAGE_REPO);
  output = Option.String("--output");
  resourceGroup = Option.String("--resource-group", {
    description: "m8t-stack resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const ctx = await resolveGatewayContext({
      interactive: mode !== "json",
      subscriptionId: this.subscription,
      resourceGroup: this.resourceGroup,
    });
    const { resourceGroup, name } = parseContainerAppResourceId(ctx.containerAppResourceId);
    const currentImage = (
      await runAz([
        "containerapp", "show", "-g", resourceGroup, "-n", name,
        "--query", "properties.template.containers[0].image", "-o", "tsv",
      ])
    ).trim();
    const tags = await fetchPublicTags(this.imageRepo);
    const plan = planUpdate({ currentImage, availableTags: tags, imageRepo: this.imageRepo });
    const s = summarizePlan(plan);

    if (mode === "json") {
      this.context.stdout.write(renderJson({ gateway: name, resourceGroup, ...s, kind: plan.kind }) + "\n");
      return 0;
    }
    this.context.stdout.write(
      renderKeyValueBlock([
        { key: "gateway", value: name },
        { key: "current", value: s.current },
        { key: "available", value: s.available },
        { key: "status", value: s.verdict },
      ]) + "\n",
    );
    return 0;
  }
}
